/**
 * Tipos internos da tela de Veículos.
 *
 * Papel: aqui ficam `VeiculoNoFormulario`, `ManutencaoNoFormulario` e
 * `OficinaNoFormulario`, os formatos que o react-hook-form guarda, e os três
 * valores em branco de onde a criação parte.
 *
 * Os três são derivados dos schemas do contrato com `z.input`, e não escritos à
 * mão, porque é a entrada daqueles schemas que o formulário precisa produzir.
 */
import {
  type novaManutencaoVeiculoSchema,
  type novaOficinaSchema,
  type novoVeiculoSchema,
} from '@concierge/shared';
import { type z } from 'zod';

/** O veículo como o formulário o guarda, antes de o schema normalizar. */
export type VeiculoNoFormulario = z.input<typeof novoVeiculoSchema>;

/** A manutenção como o formulário a guarda. */
export type ManutencaoNoFormulario = z.input<typeof novaManutencaoVeiculoSchema>;

/** A oficina como o formulário a guarda. */
export type OficinaNoFormulario = z.input<typeof novaOficinaSchema>;

/**
 * Um veículo em branco, como o formulário de criação começa.
 *
 * Os campos numéricos entram como string vazia, e não como zero: zero é valor
 * legítimo em `classeBonus`, e um formulário que já nascesse com ele faria o
 * veículo entrar com a melhor classe sem que ninguém tivesse escolhido.
 *
 * `blindado` começa em falso porque marcar a caixa é ação deliberada: começar
 * marcado faria o campo passar despercebido, e o veículo entrar como blindado
 * sem que ninguém tivesse dito isso.
 */
export const VEICULO_EM_BRANCO: VeiculoNoFormulario = {
  codigo: '',
  marca: '',
  modelo: '',
  ano: '',
  placa: '',
  chassi: '',
  renavam: '',
  categoria: '',
  lotacao: '',
  fipe: '',
  classeBonus: '',
  foraDaApolice: false,
  blindado: false,
  observacao: '',
};

/** Uma manutenção em branco, como o formulário de criação começa. */
export const MANUTENCAO_EM_BRANCO: ManutencaoNoFormulario = {
  veiculoId: '',
  data: '',
  km: '',
  tipo: '',
  descricao: '',
  oficina: '',
  valor: '',
  proxData: '',
  proxKm: '',
};

/** Uma oficina em branco, como o formulário de criação começa. */
export const OFICINA_EM_BRANCO: OficinaNoFormulario = {
  nome: '',
  servico: '',
  telefone: '',
  email: '',
  endereco: '',
  observacao: '',
};
